// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/premium/components/experience/PremiumSectionNav.module.css';
import {SHOPIER_COPY} from '@app/features/premium/utils/ShopierCopy';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';

export const PREMIUM_SECTION_IDS = {
	home: 'lafon-pro-home',
	plans: 'lafon-pro-plans',
	compare: 'lafon-pro-compare',
} as const;

export function scrollToPremiumSection(section: keyof typeof PREMIUM_SECTION_IDS): void {
	document.getElementById(PREMIUM_SECTION_IDS[section])?.scrollIntoView({behavior: 'smooth', block: 'start'});
}

export type PremiumNavTarget = keyof typeof PREMIUM_SECTION_IDS | 'purchases';

export function PremiumSectionNav({
	className,
	showPurchases = false,
	active,
	onSelect = (target) => target !== 'purchases' && scrollToPremiumSection(target),
}: {
	className?: string;
	showPurchases?: boolean;
	active?: PremiumNavTarget;
	onSelect?: (target: PremiumNavTarget) => void;
}) {
	const {i18n} = useLingui();
	const links: Array<readonly [PremiumNavTarget, string]> = [
		['home', i18n._(SHOPIER_COPY.navHome)],
		['plans', i18n._(SHOPIER_COPY.navPlans)],
		['compare', i18n._(SHOPIER_COPY.navCompare)],
		...(showPurchases ? ([['purchases', i18n._(SHOPIER_COPY.navPurchases)]] as const) : []),
	];
	return (
		<nav className={clsx(styles.nav, className)} data-flx="premium.section-nav">
			{links.map(([key, label]) => (
				<button
					key={key}
					type="button"
					className={clsx(styles.link, active === key && styles.linkActive)}
					aria-current={active === key ? 'page' : undefined}
					onClick={() => onSelect(key)}
					data-flx={`premium.section-nav.${key}`}
				>
					{label}
				</button>
			))}
		</nav>
	);
}
